import React, { useState, useRef, useEffect } from 'react';

const Tooltip = ({
    children,
    content,
    position = 'top',
    delay = 200,
    className = '',
    maxWidth = 'lg' // 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', etc., o valor personalizado como '300px' o '25rem'
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const wrapperRef = useRef(null);
    const timeoutRef = useRef(null);

    const showTooltip = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            setIsVisible(true);
        }, delay);
    };

    const hideTooltip = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setIsVisible(false);
    };

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    if (!content) {
        return children;
    }

    // Clases CSS para posicionamiento
    const positionClasses = {
        top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
        bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
        left: 'right-full top-1/2 -translate-y-1/2 mr-2',
        right: 'left-full top-1/2 -translate-y-1/2 ml-2'
    };

    // Clases para la flecha del tooltip
    const arrowClasses = {
        top: 'top-full left-1/2 -translate-x-1/2 border-t-gray-900 border-l-transparent border-r-transparent border-b-transparent',
        bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-gray-900 border-l-transparent border-r-transparent border-t-transparent',
        left: 'left-full top-1/2 -translate-y-1/2 border-l-gray-900 border-t-transparent border-b-transparent border-r-transparent',
        right: 'right-full top-1/2 -translate-y-1/2 border-r-gray-900 border-t-transparent border-b-transparent border-l-transparent'
    };

    // Mapeo de tamaños a valores en rem (para estilos inline)
    const maxWidthValues = {
        'xs': '20rem',    // 320px
        'sm': '24rem',    // 384px
        'md': '28rem',    // 448px
        'lg': '32rem',    // 512px
        'xl': '36rem',    // 576px
        '2xl': '42rem',   // 672px
        '3xl': '48rem',   // 768px
        '4xl': '56rem',   // 896px
        '5xl': '64rem',   // 1024px
        '6xl': '72rem',   // 1152px
        '7xl': '80rem'    // 1280px
    };

    // Obtener el valor de max-width para el estilo inline
    const getMaxWidthStyle = () => {
        if (!maxWidth) return { maxWidth: maxWidthValues.xs };

        if (typeof maxWidth !== 'string') return { maxWidth: maxWidthValues.xs };

        // Si ya incluye px, rem, em, etc., usarlo directamente
        if (maxWidth.includes('px') || maxWidth.includes('rem') || maxWidth.includes('em') || maxWidth.includes('%')) {
            return { maxWidth };
        }

        // Si es una de las clases predefinidas, usar el valor correspondiente
        if (maxWidthValues[maxWidth]) {
            return { maxWidth: maxWidthValues[maxWidth] };
        }

        // Si comienza con max-w-, extraer el tamaño
        if (maxWidth.startsWith('max-w-')) {
            const size = maxWidth.replace('max-w-', '');
            if (maxWidthValues[size]) {
                return { maxWidth: maxWidthValues[size] };
            }
        }

        // Por defecto
        return { maxWidth: maxWidthValues.xs };
    };

    return (
        <div
            ref={wrapperRef}
            className={`relative inline-block ${className}`}
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
            onTouchStart={showTooltip}
            onTouchEnd={hideTooltip}
            onFocus={showTooltip}
            onBlur={hideTooltip}
        >
            {children}
            {isVisible && (
                <div
                    className={`absolute z-50 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg pointer-events-none ${positionClasses[position]}`}
                    role="tooltip"
                    style={{ whiteSpace: 'normal', ...getMaxWidthStyle() }}
                >
                    <div className="text-center">{content}</div>
                    <div
                        className={`absolute w-0 h-0 border-4 ${arrowClasses[position]}`}
                    />
                </div>
            )}
        </div>
    );
};

export default Tooltip;

